import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getProjects, requestProject } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  FolderKanban, 
  Search, 
  ArrowRight,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';

export default function ClientProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    fetchProjects();
  }, [statusFilter]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const status = statusFilter === 'all' ? undefined : statusFilter;
      const response = await getProjects(status);
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleStartProject = async (e) => {
    e.preventDefault();
    try {
      await requestProject(newProject);
      toast.success('Project request submitted! Our team will review it shortly.');
      setCreateDialogOpen(false);
      setNewProject({ name: '', description: '' });
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit project request');
    }
  };

  const filteredProjects = projects.filter(project => 
    project.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="client-projects-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">My Projects</h1>
          <p className="text-slate-400">View and manage your projects</p>
        </div>
        
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#4A90D9] hover:bg-[#3A80C9]" data-testid="start-project-btn">
              <Plus className="w-4 h-4 mr-2" />
              Start New Project
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#111827] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Start a New Project</DialogTitle>
              <DialogDescription className="text-slate-400">
                Submit a project request and our team will get in touch with you.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleStartProject} className="space-y-4">
              <div>
                <Label className="text-slate-300">Project Name *</Label>
                <Input
                  value={newProject.name}
                  onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                  required
                  placeholder="e.g., Website Redesign, Marketing Campaign"
                  className="mt-1 bg-[#1E293B] border-slate-700 text-white"
                  data-testid="new-project-name"
                />
              </div>
              <div>
                <Label className="text-slate-300">Project Description</Label>
                <Textarea
                  value={newProject.description}
                  onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  placeholder="Describe what you're looking for, goals, timeline, budget, etc."
                  className="mt-1 bg-[#1E293B] border-slate-700 text-white min-h-[120px]"
                  data-testid="new-project-description"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="ghost" 
                  onClick={() => setCreateDialogOpen(false)}
                  className="text-slate-400"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-[#4A90D9] hover:bg-[#3A80C9]"
                  data-testid="submit-project-request"
                >
                  Submit Request
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="bg-[#111827] border-white/10">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#1E293B] border-slate-700 text-white"
                data-testid="search-projects"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48 bg-[#1E293B] border-slate-700 text-white" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-slate-700">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="awaiting_approval">Awaiting Approval</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Projects Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : filteredProjects.length === 0 ? (
        <Card className="bg-[#111827] border-white/10">
          <CardContent className="py-16">
            <div className="empty-state">
              <FolderKanban className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 mb-4">No projects found</p>
              <Button 
                onClick={() => setCreateDialogOpen(true)}
                className="bg-[#4A90D9] hover:bg-[#3A80C9]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Start Your First Project
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="projects-grid">
          {filteredProjects.map((project, index) => (
            <Card 
              key={project.id}
              className="bg-[#111827] border-white/10 card-hover animate-fade-in-up opacity-0"
              style={{ animationDelay: `${0.05 * index}s` }}
              data-testid={`project-card-${project.id}`}
            >
              <CardContent className="p-6">
                <div className="mb-4">
                  <h3 className="text-lg font-bold text-white truncate">{project.name}</h3>
                </div>

                {project.description && (
                  <p className="text-sm text-slate-400 line-clamp-2 mb-4">
                    {project.description}
                  </p>
                )}

                <div className="flex items-center gap-2 mb-4">
                  <Badge className={`badge-${project.status}`}>
                    {project.status.replace('_', ' ')}
                  </Badge>
                  <Badge className={`badge-${project.payment_status}`}>
                    {project.payment_status}
                  </Badge>
                </div>

                {project.payment_amount && (
                  <div className="text-sm text-slate-400 mb-4">
                    Amount: <span className="text-white font-medium">${project.payment_amount.toFixed(2)}</span>
                  </div>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-white/10">
                  <span className="text-xs text-slate-500">
                    Updated {new Date(project.updated_at).toLocaleDateString()}
                  </span>
                  <Link to={`/dashboard/projects/${project.id}`}>
                    <Button variant="ghost" size="sm" className="text-[#4A90D9] hover:text-[#38BDF8]">
                      View <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
